import { useState, useEffect, useRef, useCallback } from 'react';
import { HyperFormula } from 'hyperformula';
import { devLog } from '../utils/devLogger';

interface MemberHierarchy {
  [key: string]: {
    name: string;
    children: Array<{ name: string }>;
    parent: string | null;
  };
}

interface MemberCalculationRule {
  sources: string[];
  weights: number[];
  parent: string | null;
}

interface VerticalCalculation {
  sources?: string[];
  weights?: number[];
  type?: string;
  count?: number;
}

interface InputInfo {
  element: HTMLInputElement;
  memberName: string;
  qname: string;
  contextRef: string;
  cellKey: string;
  cellAddress: any;
  isParent: boolean;
  verticalType: string | null;
}

interface RowData {
  inputs: InputInfo[];
  inputsByQname: Map<string, InputInfo[]>;
  readonlyFields: InputInfo[];
  verticalFields: InputInfo[];
  hasVerticalCalculation: boolean;
}

interface UseHyperFormulaSOCIEReturn {
  engine: HyperFormula | null;
  isReady: boolean;
  readOnlyFields: Set<string>;
  handleFieldChange: (fieldName: string, contextRef: string, value: string) => void;
  getCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  refreshAllCalculations: () => void;
}

/**
 * HyperFormula hook for Statement of Changes in Equity (SOCIE)
 * Handles both horizontal (across equity members) and vertical (across time periods) calculations
 */
export const useHyperFormulaSOCIE = (
  formStructure: any = null
): UseHyperFormulaSOCIEReturn => {
  const [engine, setEngine] = useState<HyperFormula | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Refs to store calculation data (like the original class properties)
  const sheetRef = useRef<number>(0);
  const cellMapRef = useRef<Record<string, any>>({});
  const inputsByRowRef = useRef<Map<number, RowData>>(new Map());
  const rowIndicesRef = useRef<Map<HTMLTableRowElement, number>>(new Map());
  const memberHierarchyRef = useRef<MemberHierarchy | null>(null);
  const memberCalculationRulesRef = useRef<Record<string, MemberCalculationRule>>({});
  const verticalCalculationsRef = useRef<Record<string, VerticalCalculation>>({});
  const updatingCellRef = useRef<boolean>(false);
  const readOnlyFieldsRef = useRef<Set<string>>(new Set());

  // Format number exactly like original script
  const formatNumber = useCallback((value: any): string => {
    if (typeof value !== 'number') {
      value = 0;
    }

    // Use numeral.js if available
    if (typeof window !== 'undefined' && (window as any).numeral) {
      return (window as any).numeral(value).format('0,0.00');
    }

    // Basic fallback
    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }, []);

  // Extract hierarchy from form structure
  const extractHierarchyFromFormStructure = useCallback(() => {
    const hierarchy: MemberHierarchy = {};
    

    if (!formStructure) {
      console.warn("formStructure not available, falling back to DOM-based hierarchy");
      fallbackToDOM(hierarchy);
      return;
    }

    try {
      let disclosureSection = null;
      
      // Try with ID prefix first (from full formStructure)
      disclosureSection = formStructure.sections?.["610000_DisclosureOfChangesInEquityAbstract"] ||
        formStructure.sections?.["610000_DisclosureOnStatementOfChangesInFundAbstract"];
      
      // If not found, try without ID prefix (from SOCIETable which passes just section.name)
      if (!disclosureSection && formStructure.sections) {
        disclosureSection = formStructure.sections["DisclosureOfChangesInEquityAbstract"] ||
          formStructure.sections["DisclosureOnStatementOfChangesInFundAbstract"] ||
          formStructure.sections["StatementOfChangesInEquityAbstract"] ||
          formStructure.sections["StatementOfChangesInFundAbstract"];
      }

      if (!disclosureSection) {
        // Try to find it by checking if any section value has the right name
        if (formStructure.sections) {
          for (const [key, value] of Object.entries(formStructure.sections)) {
            if (value && typeof value === 'object' && 'name' in value) {
              const sectionName = (value as any).name;
              if (sectionName && (
                sectionName.includes('DisclosureOfChangesInEquity') ||
                sectionName.includes('DisclosureOnStatementOfChangesInFund') ||
                sectionName.includes('StatementOfChangesInEquity') ||
                sectionName.includes('StatementOfChangesInFund')
              )) {
                disclosureSection = value;
                break;
              }
            }
          }
        }
        
        if (!disclosureSection) {
          fallbackToDOM(hierarchy);
          return;
        }
      }

      const equityMemberNode = navigateToEquityMember(disclosureSection);

      if (!equityMemberNode) {
        fallbackToDOM(hierarchy);
        return;
      }

      buildHierarchyFromNode(equityMemberNode, hierarchy);
      memberHierarchyRef.current = hierarchy;
    } catch (error) {
      console.error("Error extracting hierarchy from formStructure:", error);
      fallbackToDOM(hierarchy);
    }
  }, [formStructure]);

  const navigateToEquityMember = useCallback((disclosureSection: any) => {
    try {
      const path = [
        'structure', 'children', 0,
        'structure', 'children', 0,
        'structure', 'children', 1,
        'structure', 'children', 0
      ];

      let current = disclosureSection;

      for (let i = 0; i < path.length; i += 2) {
        const prop = path[i];
        const index = path[i + 1];

        if (!current[prop] || !current[prop][index]) {
          throw new Error(`Navigation failed at ${prop}[${index}]`);
        }

        current = current[prop][index];
      }

      if (current.name === 'EquityMember' || current.name.includes('Equity') || current.name.includes('FundsAndReservesMember')) {
        return current;
      }
    } catch (e) {
      // Fallback: search for it
    }

    return findEquityMemberNode(disclosureSection);
  }, []);

  const findEquityMemberNode = useCallback((node: any, path: string[] = []): any => {
    if (!node) return null;

    if (node.name === 'EquityMember' || node.name === 'FundsAndReservesMember' ||
      (node.name &&
        node.name.endsWith('Member') &&
        node.name.includes('Equity') &&
        !node.parent)) {
      return node;
    }

    if (node.structure && node.structure.children) {
      for (let i = 0; i < node.structure.children.length; i++) {
        const result = findEquityMemberNode(node.structure.children[i], [...path, `children[${i}]`]);
        if (result) return result;
      }
    }

    for (const prop of ['children', 'members', 'items']) {
      if (node[prop] && Array.isArray(node[prop])) {
        for (let i = 0; i < node[prop].length; i++) {
          const result = findEquityMemberNode(node[prop][i], [...path, `${prop}[${i}]`]);
          if (result) return result;
        }
      }
    }

    return null;
  }, []);

  const buildHierarchyFromNode = useCallback((node: any, hierarchy: MemberHierarchy, parentName: string | null = null) => {
    if (!node || !node.name) return;

    const memberName = node.name;

    if (!hierarchy[memberName]) {
      hierarchy[memberName] = {
        name: memberName,
        children: [],
        parent: parentName
      };
    }

    if (node.structure && node.structure.children) {
      node.structure.children.forEach((child: any) => {
        if (child.name && child.name.endsWith('Member')) {
          const childExists = hierarchy[memberName].children.some(c => c.name === child.name);
          if (!childExists) {
            hierarchy[memberName].children.push({ name: child.name });
          }

          buildHierarchyFromNode(child, hierarchy, memberName);
        }
      });
    }
  }, []);

  const fallbackToDOM = useCallback((hierarchy: MemberHierarchy) => {
    const members = new Set<string>();
    document.querySelectorAll('input[data-equity-member]').forEach(input => {
      const member = (input as HTMLInputElement).dataset.equityMember;
      if (member) members.add(member);
    });

    members.forEach(memberName => {
      hierarchy[memberName] = {
        name: memberName,
        children: [],
        parent: null
      };
    });

    if (!hierarchy['EquityMember']) {
      hierarchy['EquityMember'] = {
        name: 'EquityMember',
        children: [],
        parent: null
      };
    }

    document.querySelectorAll('input[data-equity-member][data-equity-parent]').forEach(input => {
      const inp = input as HTMLInputElement;
      const memberName = inp.dataset.equityMember;
      const parentName = inp.dataset.equityParent;

      if (memberName && parentName && members.has(memberName) && members.has(parentName)) {
        hierarchy[memberName].parent = parentName;

        const alreadyChild = hierarchy[parentName].children.some(c => c.name === memberName);
        if (!alreadyChild) {
          hierarchy[parentName].children.push({ name: memberName });
        }
      }
    });

    members.forEach(memberName => {
      if (!hierarchy[memberName].parent && memberName !== 'EquityMember') {
        const potentialParents = Array.from(members)
          .filter(m => m !== memberName && memberName.includes(m))
          .sort((a, b) => b.length - a.length);

        if (potentialParents.length > 0) {
          const parentName = potentialParents[0];
          hierarchy[memberName].parent = parentName;

          const alreadyChild = hierarchy[parentName].children.some(c => c.name === memberName);
          if (!alreadyChild) {
            hierarchy[parentName].children.push({ name: memberName });
          }
        } else {
          hierarchy[memberName].parent = 'EquityMember';

          const alreadyChild = hierarchy['EquityMember'].children.some(c => c.name === memberName);
          if (!alreadyChild) {
            hierarchy['EquityMember'].children.push({ name: memberName });
          }
        }
      }
    });

    memberHierarchyRef.current = hierarchy;
  }, []);

  // Extract vertical calculation rules
  const extractChangesInEquityRules = useCallback(() => {
    if (!formStructure) {
      console.warn("formStructure not available, using default rules");
      verticalCalculationsRef.current['ChangesInEquity'] = {
        sources: ['ComprehensiveIncome'],
        weights: [1.0]
      };
      verticalCalculationsRef.current['ChangesInFunds'] = {
        sources: ['ComprehensiveIncome', 'ContributionsByAndDistributionsToOwners'],
        weights: [1.0, 1.0]
      };
      return;
    }

    // Recursive search function
    const findSectionWithCalculationRules = (obj: any, targetName: string, path: string = ''): any => {
      if (!obj) return null;
      
      // Check current object
      if (obj.name === targetName && obj.validation?.calculation?.length > 0) {
        devLog(`Found ${targetName} at path: ${path}`);
        return obj;
      }
      
      // Search in structure.children
      if (obj.structure?.children) {
        for (let i = 0; i < obj.structure.children.length; i++) {
          const child = obj.structure.children[i];
          const found = findSectionWithCalculationRules(child, targetName, `${path}.structure.children[${i}]`);
          if (found) return found;
        }
      }
      
      // Search in direct children array
      if (Array.isArray(obj.children)) {
        for (let i = 0; i < obj.children.length; i++) {
          const child = obj.children[i];
          const found = findSectionWithCalculationRules(child, targetName, `${path}.children[${i}]`);
          if (found) return found;
        }
      }
      
      // Search in sections object
      if (obj.sections) {
        for (const key in obj.sections) {
          const found = findSectionWithCalculationRules(obj.sections[key], targetName, `${path}.sections.${key}`);
          if (found) return found;
        }
      }
      
      return null;
    };

    // Search for ChangesInEquity
    const changesInEquitySection = findSectionWithCalculationRules(formStructure, 'ChangesInEquity');
    if (changesInEquitySection) {
      const rules = changesInEquitySection.validation.calculation;
      const sources: string[] = [];
      const weights: number[] = [];

      rules.forEach((rule: any) => {
        if (rule.role === 'summation-source') {
          sources.push(rule.target);
          weights.push(rule.weight || 1.0);
        }
      });

      if (sources.length > 0) {
        verticalCalculationsRef.current['ChangesInEquity'] = { sources, weights };
      }
    }

    // Search for ContributionsByAndDistributionsToOwners
    const contributionsSection = findSectionWithCalculationRules(formStructure, 'ContributionsByAndDistributionsToOwners');
    if (contributionsSection) {
      const rules = contributionsSection.validation.calculation;
      const sources: string[] = [];
      const weights: number[] = [];

      rules.forEach((rule: any) => {
        if (rule.role === 'summation-source') {
          sources.push(rule.target);
          weights.push(rule.weight || 1.0);
        }
      });

      if (sources.length > 0) {
        verticalCalculationsRef.current['ContributionsByAndDistributionsToOwners'] = { sources, weights };
      }
    }

    // Set up other rules
    verticalCalculationsRef.current['ChangesInFunds'] = {
      sources: ['ComprehensiveIncome', 'ContributionsByAndDistributionsToOwners'],
      weights: [1.0, 1.0]
    };
    verticalCalculationsRef.current['ComprehensiveIncome'] = { type: 'previous-row-sum', count: 2 };
    verticalCalculationsRef.current['OpeningEquity'] = { type: 'previous-row-sum', count: 2 };
    verticalCalculationsRef.current['ClosingEquity'] = { type: 'opening-plus-changes' };
  }, [formStructure]);

  // Build member calculation rules
  const buildMemberCalculationRules = useCallback(() => {
    const calculationRules: Record<string, MemberCalculationRule> = {};

    const buildRules = (memberName: string) => {
      const memberInfo = memberHierarchyRef.current?.[memberName];
      if (!memberInfo) return;

      if (memberInfo.children && memberInfo.children.length > 0) {
        calculationRules[memberName] = {
          sources: memberInfo.children.map(child => child.name),
          weights: memberInfo.children.map(() => 1.0),
          parent: memberInfo.parent
        };

        memberInfo.children.forEach(child => {
          buildRules(child.name);
        });
      } else {
        calculationRules[memberName] = {
          sources: [],
          weights: [],
          parent: memberInfo.parent
        };
      }
    };

    if (memberHierarchyRef.current?.['EquityMember']) {
      buildRules('EquityMember');
    } else if (memberHierarchyRef.current) {
      Object.keys(memberHierarchyRef.current).forEach(memberName => {
        if (memberHierarchyRef.current![memberName].children.length > 0 &&
          !calculationRules[memberName]) {
          buildRules(memberName);
        }
      });
    }

    if (memberHierarchyRef.current) {
      Object.keys(memberHierarchyRef.current).forEach(memberName => {
        if (!calculationRules[memberName]) {
          calculationRules[memberName] = {
            sources: [],
            weights: [],
            parent: memberHierarchyRef.current![memberName].parent
          };
        }
      });
    }

    memberCalculationRulesRef.current = calculationRules;
  }, []);

  // Map inputs by row
  const mapInputsByRow = useCallback((hfEngine: HyperFormula) => {
    let cellCount = 0;

    const rows = document.querySelectorAll('.socie-table tr');
    rows.forEach((row, index) => {
      rowIndicesRef.current.set(row as HTMLTableRowElement, index);
    });

    rows.forEach(row => {
      const rowIndex = rowIndicesRef.current.get(row as HTMLTableRowElement);
      if (rowIndex === undefined) return;

      const rowData: RowData = {
        inputs: [],
        inputsByQname: new Map(),
        readonlyFields: [],
        verticalFields: [],
        hasVerticalCalculation: false
      };

      const hasVerticalCalc = row.querySelector(
        'input[name="Equity-ComprehensiveIncome"], ' +
        'input[data-equity-source="opening"], ' +
        'input[data-equity-source="changes"], ' +
        'input[data-equity-target="closing"]'
      );

      if (hasVerticalCalc) {
        rowData.hasVerticalCalculation = true;
      }

      row.querySelectorAll('input[data-equity-member][data-qname]').forEach(inputEl => {
        const input = inputEl as HTMLInputElement;
        const memberName = input.dataset.equityMember;
        const contextRef = input.dataset.contextRef;
        const qname = input.dataset.qname;

        if (!memberName || !contextRef || !qname) return;

        let verticalType: string | null = null;
        if (input.name === "Equity-ComprehensiveIncome") {
          verticalType = "comprehensive-income";
        } else if (input.name === "Equity-ContributionsByAndDistributionsToOwners") {
          verticalType = "contributions";
        } else if (input.dataset.equitySource === "opening") {
          verticalType = "opening";
        } else if (input.dataset.equitySource === "changes") {
          verticalType = "changes";
        } else if (input.dataset.equityTarget === "closing") {
          verticalType = "closing";
        }

        const cellKey = `r${rowIndex}_${memberName}_${qname}_${contextRef}`;
        const cellAddress = {
          sheet: sheetRef.current,
          col: 0,
          row: cellCount++
        };

        cellMapRef.current[cellKey] = cellAddress;

        const inputInfo: InputInfo = {
          element: input,
          memberName,
          qname,
          contextRef,
          cellKey,
          cellAddress,
          isParent: memberCalculationRulesRef.current[memberName]?.sources?.length > 0,
          verticalType
        };

        rowData.inputs.push(inputInfo);

        if (!rowData.inputsByQname.has(qname)) {
          rowData.inputsByQname.set(qname, []);
        }
        rowData.inputsByQname.get(qname)!.push(inputInfo);

        let isReadonly = false;

        // Mark parent members as readonly (they sum their children horizontally)
        if (inputInfo.isParent) {
          isReadonly = true;
        }

        // Vertical calculation fields are always readonly (SAME AS ORIGINAL SCRIPT)
        if (verticalType) {
          isReadonly = true;
          rowData.verticalFields.push(inputInfo);
        }

                if (isReadonly) {
                  input.readOnly = true;
                  rowData.readonlyFields.push(inputInfo);
                  
                  // Track readonly fields by UNIQUE key (fieldName + contextRef)
                  // NOT just field name, because different members can have different readonly status!
                  // Example: Equity-ComprehensiveIncome for EquityMember (parent) = readonly
                  //          Equity-ComprehensiveIncome for IssuedCapitalMember = editable
                  if (input.name && contextRef) {
                    const uniqueKey = `${input.name}_${contextRef}`;
                    readOnlyFieldsRef.current.add(uniqueKey);
                  }
                }
                
                let value = 0;
        if (input.value && input.value.trim() !== '') {
          if ((window as any).numeral) {
            value = (window as any).numeral(input.value).value() || 0;
          } else {
            value = parseFloat(input.value.replace(/[^\d.-]/g, '')) || 0;
          }
        }

        hfEngine.setCellContents(cellAddress, value);
      });

      if (rowData.inputs.length > 0) {
        inputsByRowRef.current.set(rowIndex, rowData);
      }
    });
  }, []);

  // Setup horizontal formulas
  const setupHorizontalFormulas = useCallback((hfEngine: HyperFormula) => {
    inputsByRowRef.current.forEach((rowData, rowIndex) => {
      if (rowData.hasVerticalCalculation) {
        return;
      }

      rowData.inputsByQname.forEach((qnameInputs, qname) => {
        const parentInputs = qnameInputs.filter(info => info.isParent);

        parentInputs.forEach(parentInfo => {
          const { memberName, cellAddress } = parentInfo;

          const rule = memberCalculationRulesRef.current[memberName];
          if (!rule || !rule.sources || !rule.sources.length) return;

          const formulaParts: string[] = [];

          rule.sources.forEach(sourceMember => {
            const sourceInfo = qnameInputs.find(info =>
              info.memberName === sourceMember
            );

            if (sourceInfo) {
              const cellRef = hfEngine.simpleCellAddressToString(sourceInfo.cellAddress, sheetRef.current);
              if (cellRef) {
                formulaParts.push(cellRef);
              }
            }
          });

          if (formulaParts.length > 0) {
            const formula = '=' + formulaParts.join('+');
            hfEngine.setCellContents(cellAddress, formula);
          }
        });
      });
    });
  }, []);

  // Setup vertical formulas (comprehensive income, opening, contributions, changes, closing)
  const setupVerticalFormulas = useCallback((hfEngine: HyperFormula) => {
    const sortedRows = Array.from(inputsByRowRef.current.entries())
      .sort(([a], [b]) => a - b);

    setupComprehensiveIncomeFormulas(hfEngine, sortedRows);
    setupOpeningEquityFormulas(hfEngine, sortedRows);
    setupContributionsFormulas(hfEngine, sortedRows);
    setupChangesInEquityFormulas(hfEngine, sortedRows);
    setupChangesInFundsFormulas(hfEngine, sortedRows);
    setupClosingEquityFormulas(hfEngine, sortedRows);
  }, []);

  const setupComprehensiveIncomeFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const comprehensiveInputs = rowData.verticalFields
        .filter(info => info.verticalType === "comprehensive-income");

      if (comprehensiveInputs.length === 0) return;

      comprehensiveInputs.forEach(compInfo => {
        const input = compInfo.element;
        const cell = input.closest('td');
        if (!cell) return;

        const column = cell.cellIndex;
        const row = cell.closest('tr');
        const table = row?.closest('table');

        if (!table) return;

        const rows = Array.from(table.querySelectorAll('tr'));
        const currentRowIndex = rows.indexOf(row as HTMLTableRowElement);

        if (currentRowIndex < 2) return;

        const prevRow1 = rows[currentRowIndex - 1];
        const prevRow2 = rows[currentRowIndex - 2];

        const prevCell1 = (prevRow1 as HTMLTableRowElement).cells[column];
        const prevCell2 = (prevRow2 as HTMLTableRowElement).cells[column];

        if (!prevCell1 || !prevCell2) return;

        const prevInput1 = prevCell1.querySelector('input[data-equity-member][data-qname]') as HTMLInputElement;
        const prevInput2 = prevCell2.querySelector('input[data-equity-member][data-qname]') as HTMLInputElement;

        if (!prevInput1 || !prevInput2) return;

        const prevRowIndex1 = rowIndicesRef.current.get(prevRow1 as HTMLTableRowElement);
        const prevRowIndex2 = rowIndicesRef.current.get(prevRow2 as HTMLTableRowElement);

        if (prevRowIndex1 === undefined || prevRowIndex2 === undefined) return;

        const prevRowData1 = inputsByRowRef.current.get(prevRowIndex1);
        const prevRowData2 = inputsByRowRef.current.get(prevRowIndex2);

        if (!prevRowData1 || !prevRowData2) return;

        const prevInputInfo1 = prevRowData1.inputs.find(info => info.element === prevInput1);
        const prevInputInfo2 = prevRowData2.inputs.find(info => info.element === prevInput2);

        if (!prevInputInfo1 || !prevInputInfo2) return;

        const cellRef1 = hfEngine.simpleCellAddressToString(prevInputInfo1.cellAddress, sheetRef.current);
        const cellRef2 = hfEngine.simpleCellAddressToString(prevInputInfo2.cellAddress, sheetRef.current);
        
        if (!cellRef1 || !cellRef2) return;

        const formulaParts = [cellRef1, cellRef2];

        const formula = '=' + formulaParts.join('+');
        hfEngine.setCellContents(compInfo.cellAddress, formula);
      });
    });
  }, []);

  const setupOpeningEquityFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const openingInputs = rowData.verticalFields
        .filter(info => info.verticalType === "opening");

      if (openingInputs.length === 0) return;

      openingInputs.forEach(openingInfo => {
        const input = openingInfo.element;
        const cell = input.closest('td');
        if (!cell) return;

        const column = cell.cellIndex;
        const row = cell.closest('tr');
        const table = row?.closest('table');

        if (!table) return;

        const rows = Array.from(table.querySelectorAll('tr'));
        const currentRowIndex = rows.indexOf(row as HTMLTableRowElement);

        const isFundBalanceRestated = input.name === "Equity-FundBalanceRestated";
        const rowsToLookAbove = isFundBalanceRestated ? 3 : 2;

        if (currentRowIndex < rowsToLookAbove) return;

        const formulaParts: string[] = [];

        for (let i = 1; i <= rowsToLookAbove; i++) {
          const prevRow = rows[currentRowIndex - i] as HTMLTableRowElement;
          const prevCell = prevRow.cells[column];

          if (!prevCell) return;

          const prevInput = prevCell.querySelector('input[data-equity-member][data-qname]') as HTMLInputElement;
          if (!prevInput) return;

          const prevRowIndex = rowIndicesRef.current.get(prevRow);

          if (prevRowIndex !== undefined) {
            const prevRowData = inputsByRowRef.current.get(prevRowIndex);
            if (prevRowData) {
              const prevInputInfo = prevRowData.inputs.find(info => info.element === prevInput);
              if (prevInputInfo) {
                const cellRef = hfEngine.simpleCellAddressToString(prevInputInfo.cellAddress, sheetRef.current);
                if (cellRef) {
                  formulaParts.push(cellRef);
                }
              }
            }
          }
        }

        if (formulaParts.length === rowsToLookAbove) {
          const formula = '=' + formulaParts.join('+');
          hfEngine.setCellContents(openingInfo.cellAddress, formula);
        }
      });
    });
  }, []);

  const setupContributionsFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    const config = verticalCalculationsRef.current['ContributionsByAndDistributionsToOwners'];
    if (!config || !config.sources || config.sources.length === 0) return;

    sortedRows.forEach(([rowIndex, rowData]) => {
      const contributionsInputs = rowData.verticalFields
        .filter(info => info.verticalType === "contributions");

      if (contributionsInputs.length === 0) return;

      contributionsInputs.forEach(contributionsInfo => {
        const contextRef = contributionsInfo.contextRef;
        const formulaParts: string[] = [];

        config.sources!.forEach((sourceName, index) => {
          const sourceInput = document.querySelector(
            `input[name="Equity-${sourceName}"][data-context-ref="${contextRef}"]`
          ) as HTMLInputElement;

          if (sourceInput) {
            const contributionsCell = contributionsInfo.element.closest('td');
            const sourceCell = sourceInput.closest('td');

            if (contributionsCell && sourceCell &&
              contributionsCell.cellIndex === sourceCell.cellIndex) {

              const sourceRowIndex = rowIndicesRef.current.get(sourceInput.closest('tr') as HTMLTableRowElement);
              if (sourceRowIndex !== undefined) {
                const sourceRowData = inputsByRowRef.current.get(sourceRowIndex);
                if (sourceRowData) {
                  const sourceInfo = sourceRowData.inputs.find(info =>
                    info.element === sourceInput
                  );

                  if (sourceInfo) {
                    const weight = config.weights![index] || 1.0;
                    const cellRef = hfEngine.simpleCellAddressToString(sourceInfo.cellAddress, sheetRef.current);

                    if (cellRef) {
                      if (weight === 1) {
                        formulaParts.push(cellRef);
                      } else {
                        formulaParts.push(`${cellRef}*${weight}`);
                      }
                    }
                  }
                }
              }
            }
          }
        });

        if (formulaParts.length > 0) {
          const formula = '=' + formulaParts.join('+');
          hfEngine.setCellContents(contributionsInfo.cellAddress, formula);
        }
      });
    });
  }, []);

  const setupChangesInEquityFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    const config = verticalCalculationsRef.current['ChangesInEquity'];
    if (!config || !config.sources || config.sources.length === 0) {
      console.warn("No ChangesInEquity calculation rules found");
      return;
    }

    sortedRows.forEach(([rowIndex, rowData]) => {
      const changesInputs = rowData.verticalFields
        .filter(info => info.verticalType === "changes" && 
                       info.element.name === "Equity-ChangesInEquity");

      if (changesInputs.length === 0) return;

      changesInputs.forEach(changesInfo => {
        const contextRef = changesInfo.contextRef;
        const formulaParts: string[] = [];

        config.sources!.forEach((sourceName, index) => {
          const sourceInput = document.querySelector(
            `input[name="Equity-${sourceName}"][data-context-ref="${contextRef}"]`
          ) as HTMLInputElement;

          if (sourceInput) {
            const changesCell = changesInfo.element.closest('td');
            const sourceCell = sourceInput.closest('td');

            if (changesCell && sourceCell &&
              changesCell.cellIndex === sourceCell.cellIndex) {

              const sourceRowIndex = rowIndicesRef.current.get(sourceInput.closest('tr') as HTMLTableRowElement);
              if (sourceRowIndex !== undefined) {
                const sourceRowData = inputsByRowRef.current.get(sourceRowIndex);
                if (sourceRowData) {
                  const sourceInfo = sourceRowData.inputs.find(info =>
                    info.element === sourceInput
                  );

                  if (sourceInfo) {
                    const weight = config.weights![index] || 1.0;
                    const cellRef = hfEngine.simpleCellAddressToString(sourceInfo.cellAddress, sheetRef.current);

                    if (cellRef) {
                      if (weight === 1) {
                        formulaParts.push(cellRef);
                      } else {
                        formulaParts.push(`${cellRef}*${weight}`);
                      }
                    }
                  }
                }
              }
            }
          }
        });

        if (formulaParts.length > 0) {
          const formula = '=' + formulaParts.join('+');
          hfEngine.setCellContents(changesInfo.cellAddress, formula);
        }
      });
    });
  }, []);

  const setupChangesInFundsFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const changesInputs = rowData.verticalFields
        .filter(info => info.verticalType === "changes" &&
          info.element.name === "Equity-ChangesInFunds");

      if (changesInputs.length === 0) return;

      changesInputs.forEach(changesInfo => {
        const contextRef = changesInfo.contextRef;
        const formulaParts: string[] = [];

        const comprehensiveInput = document.querySelector(
          `input[name="Equity-ComprehensiveIncome"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement;

        const contributionsInput = document.querySelector(
          `input[name="Equity-ContributionsByAndDistributionsToOwners"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement;

        const changesCell = changesInfo.element.closest('td');
        const columnIndex = changesCell ? changesCell.cellIndex : -1;

        [comprehensiveInput, contributionsInput].forEach(sourceInput => {
          if (sourceInput) {
            const sourceCell = sourceInput.closest('td');
            if (sourceCell && sourceCell.cellIndex === columnIndex) {
              const sourceRowIndex = rowIndicesRef.current.get(sourceInput.closest('tr') as HTMLTableRowElement);
              if (sourceRowIndex !== undefined) {
                const sourceRowData = inputsByRowRef.current.get(sourceRowIndex);
                if (sourceRowData) {
                  const sourceInfo = sourceRowData.inputs.find(info =>
                    info.element === sourceInput
                  );

                  if (sourceInfo) {
                    const cellRef = hfEngine.simpleCellAddressToString(sourceInfo.cellAddress, sheetRef.current);
                    if (cellRef) {
                      formulaParts.push(cellRef);
                    }
                  }
                }
              }
            }
          }
        });

        if (formulaParts.length > 0) {
          const formula = '=' + formulaParts.join('+');
          hfEngine.setCellContents(changesInfo.cellAddress, formula);
        }
      });
    });
  }, []);

  const setupClosingEquityFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const closingInputs = rowData.verticalFields
        .filter(info => info.verticalType === "closing");

      if (closingInputs.length === 0) return;

      closingInputs.forEach(closingInfo => {
        const memberName = closingInfo.memberName;
        const closingElement = closingInfo.element;

        const currentRow = closingElement.closest('tr');
        if (!currentRow) return;

        const currentCell = closingElement.closest('td');
        if (!currentCell) return;

        const columnIndex = currentCell.cellIndex;

        const previousInputs: { opening: HTMLInputElement | null; changes: HTMLInputElement | null } = {
          opening: null,
          changes: null
        };

        let currentElement: Element | null = currentRow;
        while (currentElement?.previousElementSibling &&
          (!previousInputs.opening || !previousInputs.changes)) {

          const prevRow = currentElement.previousElementSibling as HTMLTableRowElement;
          if (prevRow.cells.length > columnIndex) {
            const cell = prevRow.cells[columnIndex];
            const rowInputs = cell.querySelectorAll('input');

            rowInputs.forEach(input => {
              const inp = input as HTMLInputElement;
              if (inp.dataset.equityMember === memberName) {
                if (!previousInputs.opening && inp.dataset.equitySource === 'opening') {
                  previousInputs.opening = inp;
                }
                if (!previousInputs.changes && inp.dataset.equitySource === 'changes') {
                  previousInputs.changes = inp;
                }
              }
            });
          }

          currentElement = currentElement.previousElementSibling;
        }

        if (previousInputs.opening && previousInputs.changes) {
          const openingRowIndex = rowIndicesRef.current.get(previousInputs.opening.closest('tr') as HTMLTableRowElement);
          const changesRowIndex = rowIndicesRef.current.get(previousInputs.changes.closest('tr') as HTMLTableRowElement);

          let openingInfo: InputInfo | undefined = undefined;
          let changesInfoFound: InputInfo | undefined = undefined;

          if (openingRowIndex !== undefined) {
            const openingRowData = inputsByRowRef.current.get(openingRowIndex);
            if (openingRowData) {
              openingInfo = openingRowData.inputs.find(info =>
                info.element === previousInputs.opening
              );
            }
          }

          if (changesRowIndex !== undefined) {
            const changesRowData = inputsByRowRef.current.get(changesRowIndex);
            if (changesRowData) {
              changesInfoFound = changesRowData.inputs.find(info =>
                info.element === previousInputs.changes
              );
            }
          }

          if (openingInfo && changesInfoFound) {
            const openingCellRef = hfEngine.simpleCellAddressToString(openingInfo.cellAddress, sheetRef.current);
            const changesCellRef = hfEngine.simpleCellAddressToString(changesInfoFound.cellAddress, sheetRef.current);
            
            if (openingCellRef && changesCellRef) {
              const formula = `=${openingCellRef}+${changesCellRef}`;
              hfEngine.setCellContents(closingInfo.cellAddress, formula);
            }
          }
        }
      });
    });
  }, []);

  // Refresh horizontal calculations in a row
  const refreshHorizontalCalculationsInRow = useCallback((rowIndex: number, qname: string) => {
    if (!engine) return;

    const rowData = inputsByRowRef.current.get(rowIndex);
    if (!rowData) return;

    if (rowData.hasVerticalCalculation) return;

    const readonlyFields = rowData.readonlyFields.filter(info =>
      info.qname === qname && info.isParent
    );

    requestAnimationFrame(() => {
      readonlyFields.forEach(info => {
        const cell = info.cellAddress;
        const input = info.element;

        const value = engine.getCellValue(cell);
        const formattedValue = formatNumber(value);

        if (input.value !== formattedValue) {
          input.value = formattedValue;
          
          // Dispatch custom event to notify React (like SOFP does)
          input.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
            detail: { 
              fieldName: input.name,
              contextRef: input.dataset.contextRef,
              value: formattedValue 
            }
          }));
        }
      });
    });
  }, [engine, formatNumber]);

  // Refresh vertical calculations
  const refreshVerticalCalculations = useCallback(() => {
    if (!engine) return;

    requestAnimationFrame(() => {
      inputsByRowRef.current.forEach((rowData, rowIndex) => {
        if (rowData.verticalFields.length === 0) return;

        rowData.verticalFields.forEach(info => {
          const cell = info.cellAddress;
          const input = info.element;

          const value = engine.getCellValue(cell);
          const formattedValue = formatNumber(value);

          if (input.value !== formattedValue) {
            input.value = formattedValue;
            
            // Dispatch custom event to notify React (like SOFP does)
            input.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
              detail: { 
                fieldName: input.name,
                contextRef: input.dataset.contextRef,
                value: formattedValue 
              }
            }));
          }
        });
      });
    });
  }, [engine, formatNumber]);

  // Refresh row calculations
  const refreshRowCalculations = useCallback((rowIndex: number, qname: string) => {
    refreshHorizontalCalculationsInRow(rowIndex, qname);
    refreshVerticalCalculations();
  }, [refreshHorizontalCalculationsInRow, refreshVerticalCalculations]);

  // Handle input change
  const handleInputChange = useCallback((input: HTMLInputElement) => {
    if (updatingCellRef.current || !engine) return;
    updatingCellRef.current = true;

    try {
      const memberName = input.dataset.equityMember;
      const contextRef = input.dataset.contextRef;
      const qname = input.dataset.qname;

      if (!memberName || !contextRef || !qname) return;

      const row = input.closest('tr');
      if (!row) return;

      const rowIndex = rowIndicesRef.current.get(row as HTMLTableRowElement);
      if (rowIndex === undefined) return;

      const rowData = inputsByRowRef.current.get(rowIndex);
      if (!rowData) return;

      const inputInfo = rowData.inputs.find(info =>
        info.element === input ||
        (info.memberName === memberName &&
          info.qname === qname &&
          info.contextRef === contextRef)
      );

      if (!inputInfo) return;

      let value = 0;
      if (input.value && input.value.trim() !== '') {
        if ((window as any).numeral) {
          value = (window as any).numeral(input.value).value() || 0;
        } else {
          value = parseFloat(input.value.replace(/[^\d.-]/g, '')) || 0;
        }
      }

      engine.setCellContents(inputInfo.cellAddress, value);
      refreshRowCalculations(rowIndex, qname);
    } catch (error) {
      console.error("Error handling input change:", error);
    } finally {
      updatingCellRef.current = false;
    }
  }, [engine, refreshRowCalculations]);

  // Refresh all calculations
  const refreshAllCalculations = useCallback(() => {
    if (!engine) return;

    // Refresh horizontal calculations
    inputsByRowRef.current.forEach((rowData, rowIndex) => {
      if (rowData.hasVerticalCalculation) return;

      const qnames = new Set<string>();
      rowData.inputs.forEach(info => qnames.add(info.qname));

      qnames.forEach(qname => {
        refreshHorizontalCalculationsInRow(rowIndex, qname);
      });
    });

    // Refresh vertical calculations
    refreshVerticalCalculations();
  }, [engine, refreshHorizontalCalculationsInRow, refreshVerticalCalculations]);

  // Public API: handleFieldChange (wraps handleInputChange for external use)
  const handleFieldChangeAPI = useCallback((fieldName: string, contextRef: string, value: string) => {
    if (!engine) return;
    
    // Find the input element with matching name and context-ref
    const input = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement;
    
    if (input) {
      // Update the input value first
      input.value = value;
      // Then trigger the change handler
      handleInputChange(input);
    }
  }, [engine, handleInputChange]);

  // Public API: getCalculatedValue
  const getCalculatedValueAPI = useCallback((fieldName: string, contextRef: string): number | null => {
    if (!engine || !isReady) return null;
    
    // Find the cell for this field and context
    // Search through all rows for a matching input
    for (const [rowIndex, rowData] of Array.from(inputsByRowRef.current.entries())) {
      const inputInfo = rowData.inputs.find((info: InputInfo) => {
        const inputFieldName = info.element.name;
        const inputContextRef = info.element.dataset.contextRef;
        return inputFieldName === fieldName && inputContextRef === contextRef;
      });
      
      if (inputInfo) {
        const value = engine.getCellValue(inputInfo.cellAddress);
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const numValue = parseFloat(value);
          return isNaN(numValue) ? null : numValue;
        }
        return null;
      }
    }
    
    return null;
  }, [engine, isReady]);

  // Setup event listeners
  useEffect(() => {
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.matches('.socie-table input[data-equity-member][data-qname]:not([readonly])')) {
        handleInputChange(target);
      }
    };

    const handleChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.matches('.socie-table input[data-equity-member][data-qname]:not([readonly])')) {
        handleInputChange(target);
      }
    };

    document.addEventListener('input', handleInput);
    document.addEventListener('change', handleChange);

    return () => {
      document.removeEventListener('input', handleInput);
      document.removeEventListener('change', handleChange);
    };
  }, [handleInputChange]);

  // Initialize HyperFormula engine
  useEffect(() => {
    if (engine) return; // Already initialized

    try {
      const hfEngine = HyperFormula.buildEmpty({
        licenseKey: 'gpl-v3',
        precisionRounding: 10,
        useColumnIndex: false
      });

      const sheetName = hfEngine.addSheet('socie');
      const sheetId = hfEngine.getSheetId(sheetName);
      sheetRef.current = sheetId || 0;

      // Extract hierarchy
      extractHierarchyFromFormStructure();

      // Extract vertical calculation rules
      extractChangesInEquityRules();

      // Build calculation rules
      buildMemberCalculationRules();

      // Set engine first (needed for calculations)
      setEngine(hfEngine);

      // Delay mapping until DOM is ready
      setTimeout(() => {
        const socieTable = document.querySelector('.socie-table');
        if (!socieTable) {
          console.warn('SOCIE table not found, skipping initialization');
          setIsReady(false);
          return;
        }

        // Map inputs by row
        mapInputsByRow(hfEngine);

        // Setup horizontal formulas
        setupHorizontalFormulas(hfEngine);

        // Setup vertical formulas
        setupVerticalFormulas(hfEngine);

        // Initial calculation - populate all readonly fields with 0.00
        // Force initial display by directly updating DOM
        inputsByRowRef.current.forEach((rowData, rowIndex) => {
          // Refresh horizontal calculations
          if (!rowData.hasVerticalCalculation) {
            rowData.readonlyFields.forEach(info => {
              if (info.isParent) {
                const value = hfEngine.getCellValue(info.cellAddress);
                const formattedValue = formatNumber(value);
                info.element.value = formattedValue;
                
                // Dispatch event to notify React
                info.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
                  detail: { 
                    fieldName: info.element.name,
                    contextRef: info.element.dataset.contextRef,
                    value: formattedValue 
                  }
                }));
              }
            });
          }
          
          // Refresh vertical calculations
          rowData.verticalFields.forEach(info => {
            const value = hfEngine.getCellValue(info.cellAddress);
            const formattedValue = formatNumber(value);
            info.element.value = formattedValue;
            
            // Dispatch event to notify React
            info.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
              detail: { 
                fieldName: info.element.name,
                contextRef: info.element.dataset.contextRef,
                value: formattedValue 
              }
            }));
          });
        });

        // Mark as ready AFTER all initialization is complete
        setIsReady(true);
      }, 500);
    } catch (error) {
      console.error("Error initializing SOCIE calculator:", error);
      setIsReady(false);
    }
  }, []); // Empty dependency array - only run once

  // Listen for formDataLoaded event to re-read from DOM (like mbrs 1.0)
  useEffect(() => {
    if (!engine) return;

    const handleFormDataLoaded = () => {
      devLog('HyperFormula SOCIE: formDataLoaded event - fast sync (no remap)...');

      // Small delay expected (EditFilings writes DOM first)
      setTimeout(() => {
        // Update ONLY editable cells from current DOM values (keep formulas intact)
        inputsByRowRef.current.forEach((rowData) => {
          rowData.inputs.forEach((info) => {
            // Skip readonly (parent/vertical) cells - they contain formulas
            if (info.element.readOnly) return;

            let value = 0;
            const raw = info.element.value;
            if (raw && raw.trim() !== '') {
              if ((window as any).numeral) {
                value = (window as any).numeral(raw).value() || 0;
              } else {
                value = parseFloat(raw.replace(/[^\d.-]/g, '')) || 0;
              }
            }

            engine.setCellContents(info.cellAddress, value);
          });
        });

        // Refresh calculated (readonly) fields
        refreshAllCalculations();

        devLog('HyperFormula SOCIE: fast sync complete');

        // 🔒 FIX: Wait for event loop to settle before signaling completion
        // On fast machines, the DOM might not have updated yet
        // Use requestAnimationFrame sequence to ensure browser has processed all updates
        let rafCount = 0;
        const waitForSettled = () => {
          rafCount++;
          if (rafCount >= 3) {
            // After RAF calls, add small delay then signal completion
            setTimeout(() => {
              document.dispatchEvent(new CustomEvent('socieCalculationsComplete'));
            }, 200);
          } else {
            requestAnimationFrame(waitForSettled);
          }
        };
        requestAnimationFrame(waitForSettled);
      }, 100);  // Increased from 0 to 100ms
    };
    
    document.addEventListener('formDataLoaded', handleFormDataLoaded);
    
    return () => {
      document.removeEventListener('formDataLoaded', handleFormDataLoaded);
    };
  }, [engine, mapInputsByRow, setupHorizontalFormulas, setupVerticalFormulas, formatNumber]);

  return {
    engine,
    isReady,
    readOnlyFields: readOnlyFieldsRef.current,
    handleFieldChange: handleFieldChangeAPI,
    getCalculatedValue: getCalculatedValueAPI,
    refreshAllCalculations,
  };
};
